import { readAuthToken } from "../utils/authStorage";

export const API_BASE_URL = process.env.NODE_ENV === "production" ? (process.env.REACT_APP_API_BASE_URL || "/") : "";

/** URL prefix for all API calls (must match dev proxy path prefix, e.g. `/api` or `/api/v1`). */
export const API_PREFIX = (process.env.REACT_APP_API_PREFIX ?? "/api").replace(/\/$/, "") || "/api";

export const API_ENDPOINTS = {
  AUTH: {
    REGISTER: "/auth/register",
    LOGIN: "/auth/login",
    REFRESH: "/auth/refresh",
    LOGOUT: "/auth/logout",
    FORGOT_PASSWORD: "/auth/forgot-password",
    RESET_PASSWORD: "/auth/reset-password",
  },
  USERS: {
    ME: "/users/me",
    ME_PASSWORD: "/users/me/password",
    ME_AVATAR: "/users/me/avatar",
    NOTIFICATION_PREFERENCES: "/users/me/notification-preferences",
    COMPANY: "/users/company",
  },
  EVENTS: {
    /** FastAPI lists on GET /events/ but creates on POST /events — trailing slash matters. */
    LIST: "/events/",
    CREATE: "/events",
    IMPORT: "/events/import",
    STATUSES: "/events/statuses",
    DETAIL: (id: string) => `/events/${id}`,
    UPDATE: (id: string) => `/events/${id}`,
    DELETE: (id: string) => `/events/${id}`,
    TICKET_TIERS: (eventId: string) => `/events/${eventId}/ticket-tiers`,
    TICKET_TIER: (eventId: string, tierId: string) => `/events/${eventId}/ticket-tiers/${tierId}`,
    FINANCES: (id: string) => `/events/${id}/finances`,
    CALCULATE_PNL: "/events/calculate-pnl",
    DASHBOARD: (id: string) => `/events/dashboard/${id}`,
    FINANCE_OVERVIEW: (id: string) => `/events/${id}/finance-overview`,
    FORECAST_CURVE: (id: string) => `/events/${id}/pnl/forecast-curve`,
    AI_INSIGHTS: (id: string) => `/events/${id}/ai-insights`,
    STAFFING_ESTIMATE: "/events/staffing-estimate",
    REVIEW: (eventId: string) => `/events/${eventId}/review`,
    REVIEW_REFRESH: (eventId: string) => `/events/${eventId}/review/refresh`,
  },
  GEO: {
    PINCODE: (pin: string) => `/geo/pincode/${pin}`,
  },
  NOTIFICATIONS: {
    PREFERENCES: "/users/me/notification-preferences",
    WHATSAPP_VERIFY_REQUEST: "/users/me/notification-preferences/whatsapp/verify-request",
    WHATSAPP_VERIFY: "/users/me/notification-preferences/whatsapp/verify",
    WHATSAPP: "/users/me/notification-preferences/whatsapp",
  },
  INVOICES: {
    GENERATE: "/invoices/generate",
    DETAIL: (id: string) => `/invoices/${id}`,
    BY_EVENT: (eventId: string) => `/invoices/by-event/${eventId}`,
    SEARCH: "/invoices/search",
    DRAFTS: "/invoices/drafts",
    UPDATE: (id: string) => `/invoices/${id}`,
    FINALIZE: (id: string) => `/invoices/${id}/finalize`,
    DELETE: (id: string) => `/invoices/${id}`,
    STATUS: (id: string) => `/invoices/${id}/status`,
    PDF: (id: string) => `/invoices/${id}/pdf`,
    EMAIL: (id: string) => `/invoices/${id}/email`,
    BULK_DOWNLOAD: "/invoices/bulk-download",
  },
  PORTFOLIO: {
    METRICS: "/portfolio/metrics",
    SUMMARY: "/portfolio/summary",
    INSIGHTS: "/portfolio/insights",
    UPCOMING_PAYOUTS: "/portfolio/upcoming-payouts",
    TRANSACTIONS: "/portfolio/transactions",
  },
  ARTISTS: {
    LIST: "/artists",
    CREATE: "/artists",
    DETAIL: (id: string) => `/artists/${id}`,
    UPDATE: (id: string) => `/artists/${id}`,
    DELETE: (id: string) => `/artists/${id}`,
    PHOTO: (id: string) => `/artists/${id}/photo`,
  },
  CONTACTS: {
    LIST: "/contacts",
    CREATE: "/contacts",
    DETAIL: (id: string) => `/contacts/${id}`,
    UPDATE: (id: string) => `/contacts/${id}`,
    DELETE: (id: string) => `/contacts/${id}`,
    LINK_ARTISTS: (id: string) => `/contacts/${id}/artists`,
    UNLINK_ARTIST: (contactId: string, artistId: string) => `/contacts/${contactId}/artists/${artistId}`,
    PHOTO: (id: string) => `/contacts/${id}/photo`,
  },
  VENUES: {
    LIST: "/venues",
    CREATE: "/venues",
    DETAIL: (id: string) => `/venues/${id}`,
    UPDATE: (id: string) => `/venues/${id}`,
    DELETE: (id: string) => `/venues/${id}`,
    PHOTO: (id: string) => `/venues/${id}/photo`,
  },
  COMPANIES: {
    ME: "/companies/me",
    UPDATE: "/companies/me",
    LOGO: "/companies/me/logo",
    INVOICE_TEMPLATE: "/companies/me/invoice-template",
  },
  BUDGETS: {
    LIST: (eventId: string) => `/events/${eventId}/budgets`,
    CREATE: (eventId: string) => `/events/${eventId}/budgets`,
    UPDATE: (eventId: string, budgetId: string) => `/events/${eventId}/budgets/${budgetId}`,
    DELETE: (eventId: string, budgetId: string) => `/events/${eventId}/budgets/${budgetId}`,
  },
  TASKS: {
    LIST: (eventId: string) => `/events/${eventId}/tasks`,
    CREATE: (eventId: string) => `/events/${eventId}/tasks`,
    UPDATE: (eventId: string, taskId: string) => `/events/${eventId}/tasks/${taskId}`,
    DELETE: (eventId: string, taskId: string) => `/events/${eventId}/tasks/${taskId}`,
  },
  SCHEDULES: {
    LIST: (eventId: string) => `/events/${eventId}/schedules`,
    CREATE: (eventId: string) => `/events/${eventId}/schedules`,
    UPDATE: (eventId: string, scheduleId: string) => `/events/${eventId}/schedules/${scheduleId}`,
    DELETE: (eventId: string, scheduleId: string) => `/events/${eventId}/schedules/${scheduleId}`,
  },
  TICKET_ENTRIES: {
    LIST: (eventId: string) => `/events/${eventId}/ticket-entries`,
    CREATE: (eventId: string) => `/events/${eventId}/ticket-entries`,
    UPDATE: (eventId: string, entryId: string) => `/events/${eventId}/ticket-entries/${entryId}`,
  },
  TICKET_SCAN: {
    SCAN: (eventId: string) => `/events/${eventId}/scan`,
    STATS: (eventId: string) => `/events/${eventId}/scan/stats`,
    COLLECT: (eventId: string) => `/events/${eventId}/scan/collect`,
  },
  PUBLIC_PASS: {
    PASS: (token: string) => `/public/pass/${token}`,
    GUEST_STATUS: (token: string) => `/public/guest-status/${token}`,
  },
  TICKETING_PROVISION: {
    PROVISION: (eventId: string) => `/events/${eventId}/ticketing/provision`,
  },
  ACTIVITY_ANALYTICS: {
    GET: (eventId: string) => `/events/${eventId}/activity-analytics`,
  },
  TEAM_MEMBERS: {
    LIST: "/team-members",
    CREATE: "/team-members",
    UPDATE: (memberId: string) => `/team-members/${memberId}`,
    DELETE: (memberId: string) => `/team-members/${memberId}`,
  },
  TICKETING_PARTNERS: {
    LIST: "/ticketing-partners",
  },
  COMPANY_OPERATORS: {
    LIST: "/company-operators",
    CREATE: "/company-operators",
    CREDENTIALS: (userId: string) => `/company-operators/${userId}/credentials`,
  },
  EVENT_PROMOTER_ACCOUNTS: {
    LIST: (eventId: string) => `/events/${eventId}/promoter-accounts`,
    CREATE: (eventId: string) => `/events/${eventId}/promoter-accounts`,
    DELETE: (eventId: string, promoterUserId: string) => `/events/${eventId}/promoter-accounts/${promoterUserId}`,
  },
  EVENT_NOTES: {
    DETAIL: (eventId: string) => `/events/${eventId}/notes`,
  },
  EVENT_DEAL_TERMS: {
    DETAIL: (eventId: string) => `/events/${eventId}/deal-terms`,
  },
  EVENT_FILES: {
    LIST: (eventId: string) => `/events/${eventId}/files`,
    DOWNLOAD: (eventId: string, fileId: string) => `/events/${eventId}/files/${fileId}`,
    DELETE: (eventId: string, fileId: string) => `/events/${eventId}/files/${fileId}`,
  },
  ADMIN_TICKETING_LINKS: {
    LIST: "/admin/ticketing-links",
    CREATE: "/admin/ticketing-links",
    UNLINK: (linkId: string) => `/admin/ticketing-links/${linkId}`,
    BACKFILL: (linkId: string) => `/admin/ticketing-links/${linkId}/backfill`,
    COMPANIES: "/admin/ticketing-links/companies",
    COMPANY_EVENTS: (companyId: string) => `/admin/ticketing-links/companies/${companyId}/events`,
    EVENT_OS_EVENTS: "/admin/ticketing-links/event-os-events",
    TICKETING_EVENTS: "/admin/ticketing-links/ticketing-events",
    TICKETING_EVENT: (id: number) => `/admin/ticketing-links/ticketing-events/${id}`,
  },
  RSVP: {
    SETTINGS: (eventId: string) => `/events/${eventId}/rsvp`,
    GUESTS: (eventId: string) => `/events/${eventId}/rsvp/guests`,
    GUEST: (eventId: string, guestId: string) => `/events/${eventId}/rsvp/guests/${guestId}`,
    GUEST_SEARCH: (eventId: string, q: string) =>
      `/events/${eventId}/rsvp/guests/search?q=${encodeURIComponent(q)}`,
    GUEST_CHECK_IN: (eventId: string, guestId: string) =>
      `/events/${eventId}/rsvp/guests/${guestId}/check-in`,
    PROMOTER_GUESTS: "/guests/",
    PROMOTER_GUEST: (phone: string) => `/guests/${encodeURIComponent(phone)}`,
    PROMOTERS: (eventId: string) => `/events/${eventId}/promoters`,
    PROMOTER: (eventId: string, promoterId: string) =>
      `/events/${eventId}/promoters/${promoterId}`,
    PUBLIC_META: (token: string) => `/public/rsvp/${token}`,
    PUBLIC_SUBMIT: (token: string) => `/public/rsvp/${token}`,
  },
  INVITES: {
    FORM: (eventId: string) => `/events/${eventId}/invites`,
    REGENERATE_LINK: (eventId: string) => `/events/${eventId}/invites/regenerate-link`,
    REQUESTS: (eventId: string) => `/events/${eventId}/invites/requests`,
    REQUEST: (eventId: string, requestId: string) => `/events/${eventId}/invites/requests/${requestId}`,
    APPROVE: (eventId: string, requestId: string) => `/events/${eventId}/invites/requests/${requestId}/approve`,
    WAITLIST: (eventId: string, requestId: string) => `/events/${eventId}/invites/requests/${requestId}/waitlist`,
    EXPORT: (eventId: string, status: string) =>
      `/events/${eventId}/invites/requests/export.csv?status=${encodeURIComponent(status)}`,
    PUBLIC: (token: string) => `/public/invite/${token}`,
    PUBLIC_PAY: (payToken: string) => `/public/invite/pay/${payToken}`,
    PUBLIC_ME: (formToken: string, accessToken: string) =>
      `/public/invite/${formToken}/me?t=${encodeURIComponent(accessToken)}`,
    PUBLIC_ME_PAY: (formToken: string) => `/public/invite/${formToken}/me/pay`,
    PUBLIC_LOOKUP: (formToken: string) => `/public/invite/${formToken}/lookup`,
    PUBLIC_VERIFY: (formToken: string) => `/public/invite/${formToken}/verify`,
  },
};

export const getApiUrl = (endpoint: string, options?: { ensureTrailingSlash?: boolean }): string => {
  let path = `${API_PREFIX}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;
  if (options?.ensureTrailingSlash && !path.endsWith("/")) {
    path = `${path}/`;
  }
  if (API_BASE_URL === "") {
    return path;
  }
  const baseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  return `${baseUrl}${path}`;
};

/** GET collection routes where FastAPI requires a trailing slash (e.g. `/api/events/`). */
export const getCollectionListUrl = (collectionPath: string): string =>
  getApiUrl(collectionPath.replace(/\/+$/, ""), { ensureTrailingSlash: true });

export const getAuthHeaders = (token?: string): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const authToken = token || readAuthToken();
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }
  return headers;
};
